/**
 * Sentry initialization configuration
 * Works for both web and native (iOS/Android) platforms via @sentry/capacitor
 */

import * as Sentry from "@sentry/capacitor";
import { globalHandlersIntegration, browserApiErrorsIntegration } from "@sentry/browser";
import { createSentryBeforeSend, createSentryBeforeBreadcrumb } from '../utils/sentryHelpers';
import { EXCLUDED_SENTRY_ENVIRONMENTS, PRODUCTION_ENVIRONMENTS } from './sentryConstants';

/**
 * Determines if Sentry should be initialized based on environment
 * @param {string} environment - Current environment
 * @param {string} dsn - Sentry DSN
 * @returns {boolean} - True if Sentry should initialize
 */
const shouldInitializeSentry = (environment, dsn) => {
  return dsn && !EXCLUDED_SENTRY_ENVIRONMENTS.includes(environment);
};

/**
 * Determines if performance monitoring should be enabled
 * @param {string} environment - Current environment
 * @returns {boolean} - True if performance monitoring should be enabled
 */
const isProductionEnvironment = (environment) => {
  return PRODUCTION_ENVIRONMENTS.includes(environment);
};

/**
 * Initializes Sentry with appropriate configuration
 * Only initializes for staging/production/beta environments
 */
export const initializeSentry = () => {
  const environment = import.meta.env.VITE_PUBLIC_APP_ENV;
  const dsn = import.meta.env.VITE_SENTRY_DSN;

  if (!shouldInitializeSentry(environment, dsn)) {
    console.log(`Sentry initialization skipped for environment: ${environment}`);
    return;
  }

  Sentry.init({
    dsn,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || environment || 'development',
    release: `global-tasks@${import.meta.env.VITE_PUBLIC_APP_VERSION}`,
    dist: import.meta.env.VITE_BUILD_ID,
    
    // Disable all automatic error capture; only explicit captureException() calls will report.
    // GlobalHandlers: disables window.onerror / onunhandledrejection
    // BrowserApiErrors: disables monkey-patching of setTimeout, addEventListener, XHR, etc.
    integrations: (defaultIntegrations) => {
      return defaultIntegrations
        .filter((i) => i.name !== 'GlobalHandlers' && i.name !== 'BrowserApiErrors')
        .concat(
          globalHandlersIntegration({ onerror: false, onunhandledrejection: false }),
          browserApiErrorsIntegration({ setTimeout: false, setInterval: false, requestAnimationFrame: false, XMLHttpRequest: false, eventTarget: false })
        );
    },
    
    // Disable default PII collection (IP addresses, cookies, user identification)
    sendDefaultPii: false,
    
    // Performance monitoring: enabled only in production (10%), disabled in staging/dev (0%)
    tracesSampleRate: isProductionEnvironment(environment) ? 0.1 : 0.0,
    replaysSessionSampleRate: 0.0,
    replaysOnErrorSampleRate: 0.0,
    
    // Only capture errors and stack traces
    attachStacktrace: true,
    
    // Scrub PII and filter transient errors from events
    beforeSend: createSentryBeforeSend(),

    // Scrub sensitive data from breadcrumbs at capture time
    beforeBreadcrumb: createSentryBeforeBreadcrumb(),
  });

  console.log(`Sentry initialized for environment: ${environment}`);
};
